import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getMemoryBookEntries,
  getMemoryBookSingle,
  postMemoryBookAddEntry,
  postMemoryBookShare,
  updateMemoryBookSharePermission,
  deleteMemoryBook
} from "../../../services/memoryBookService";
import { getCurrentPackageUserModule } from "../../../services/api";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import ModuleHeader from "../../moduleHeader";
import { FaTrash, FaShare, FaPen, FaTimes, FaUserEdit, FaRegCalendarAlt } from 'react-icons/fa';
import { FiSend, FiPlus, FiMoreHorizontal, FiArrowLeft, FiImage } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Notiflix from "notiflix";

export default function MemoryBookInside({ data, onBack }) {
  const { id: routeId } = useParams();
  const actualId = routeId;

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [newEntry, setNewEntry] = useState({ content: "" });
  const [submitting, setSubmitting] = useState(false);
  const [book, setBook] = useState(null);
  const [sharedWith, setSharedWith] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharePermission, setSharePermission] = useState("read");
  const [isSharing, setIsSharing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);
  const [isUpdatingPermission, setIsUpdatingPermission] = useState(false);

  // Notiflix'i initialize et
  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Paket verisini çek
  useEffect(() => {
    if (!actualId) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(actualId, 10); // MemoryBook moduleId: 10
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [actualId]);

  useEffect(() => {
    if (data?.uuid) {
      fetchEntries();
    } else if (data && !data.uuid) {
      // Eğer data var ama uuid yoksa, loading'i false yap
      setLoading(false);
    }
  }, [data?.uuid]);

  const fetchEntries = async () => {
    if (!data?.uuid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const single = await getMemoryBookSingle(data.uuid);
      if (single && single.success && single.data) {
        setBook(single.data);
        setSharedWith(Array.isArray(single.data.shared_with) ? single.data.shared_with : []);
      }

      const response = await getMemoryBookEntries(data.uuid);
      if (response && response.success) {
        const entriesArr = Array.isArray(response.data?.entries) ? response.data.entries : [];
        setEntries(entriesArr);
        // memory_book üst bilgisi burada da gelebilir; önceliği single'a veriyoruz
        if (!single?.data && response.data?.memory_book) {
          setBook(response.data.memory_book);
        }
      } else {
        setEntries([]);
      }
    } catch (err) {
      console.error("Anı defteri verileri alınırken hata:", err);
      setError("Girişler alınamadı");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.content.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await postMemoryBookAddEntry(data.uuid, newEntry);
      if (response && response.success) {
        setNewEntry({ content: "" });
        setShowAddEntry(false);
        await fetchEntries();
      }
    } catch (err) {
      setError("Giriş eklenemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const startMemoryBookInvite = () => {
    setShowSharePopup(true);
  };

  const handleShareMemoryBook = async () => {
    if (!shareEmail.trim()) {
      Notiflix.Notify.failure("Lütfen e-posta adresi giriniz!");
      return;
    }

    try {
      setIsSharing(true);
      const response = await postMemoryBookShare(data.uuid, shareEmail, sharePermission);
      if (response && response.success) {
        Notiflix.Notify.success("Anı defteri başarıyla paylaşıldı!");
        setShowSharePopup(false);
        setShareEmail("");
        setSharePermission("read");
        await fetchEntries();
      }
    } catch (error) {
      console.error("Anı defteri paylaşılırken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message || "Anı defteri paylaşılırken bir hata oluştu!"
      );
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeleteMemoryBook = async () => {
    try {
      setIsDeleting(true);
      const response = await deleteMemoryBook(data.uuid);
      if (response && response.success) {
        Notiflix.Notify.success("Anı defteri başarıyla silindi!");
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Anı defteri silinirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message || "Anı defteri silinirken bir hata oluştu!"
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUserClick = (user) => {
    // is_shared false ise admin'dir, veya is_admin false değilse tıklanabilir
    if (book?.is_shared === true && book?.is_admin === false) {
      return;
    }
    // Eğer share_id yoksa (henüz paylaşım kabul edilmemişse) permission değiştirilemez
    if (!user.share_id) {
      Notiflix.Notify.info("Bu kullanıcının paylaşımı henüz kabul edilmemiş. Önce paylaşımı kabul etmesi gerekiyor.");
      return;
    }
    setSelectedUser(user);
    setShowPermissionPopup(true);
  };

  const handleUpdatePermission = async () => {
    if (!selectedUser || !selectedUser.share_id) return;

    try {
      setIsUpdatingPermission(true);
      const response = await updateMemoryBookSharePermission(
        selectedUser.share_id,
        selectedUser.permission
      );
      if (response && response.success) {
        Notiflix.Notify.success("Kullanıcı yetkisi başarıyla güncellendi!");
        setShowPermissionPopup(false);
        setSelectedUser(null);
        await fetchEntries();
      }
    } catch (error) {
      console.error("Kullanıcı yetkisi güncellenirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message || "Kullanıcı yetkisi güncellenirken bir hata oluştu!"
      );
    } finally {
      setIsUpdatingPermission(false);
    }
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setShowEntryModal(true);
  };

  const closeEntryModal = () => {
    setShowEntryModal(false);
    setSelectedEntry(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Tarih bilinmiyor";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Tarih bilinmiyor";
    }
  };

  // Paylaşım akışı: yalnızca tarama ekranı gösterilsin
  if (showScanner) {
    return (
      <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
        <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
          <div className="w-full px-4 py-4">
            <div className="max-w-sm mx-auto w-full mt-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setShowScanner(false)}
                  className="flex items-center gap-1 text-gray-600 text-sm font-medium hover:text-gray-900"
                >
                  <FiArrowLeft className="w-4 h-4" /> Geri
                </button>
              </div>
              <div className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center border border-gray-100">
                <img
                  src="/assets/pngImages/scanJodiac.png"
                  alt="Scan"
                  className="w-40 h-56 object-contain mb-8 filter drop-shadow-lg"
                />
                <div className="text-gray-800 text-lg max-w-60 font-bold text-center leading-tight">
                  Anı Defterinizi Paylaşacağınız Kişinin Jodiacını Okutunuz.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading screen - sadece packageData yüklenirken göster
  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  // Eğer data yoksa veya uuid yoksa hata göster
  if (!data || !data.uuid) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-600 mb-6 font-medium">Anı defteri verisi bulunamadı.</p>
          {onBack && (
            <button
              onClick={onBack}
              className="px-6 py-2.5 bg-[#3DA7B5] text-white rounded-xl hover:bg-[#2A8B96] transition-colors font-semibold shadow-lg shadow-[#3DA7B5]/30"
            >
              Geri Dön
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={book?.title || "Anı Defteri"}
              title={packageData.package?.name}
              description={book?.description || packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
              onBack={onBack}
            />
          </div>
        )}

        <div className="w-full px-5 py-6">
          <div className="max-w-sm mb-20 mx-auto">
            {/* Top Bar: Avatars & Actions */}
            <div className="flex items-center justify-between mb-8 sticky top-0 z-10 bg-white/80 backdrop-blur-md py-3 -mx-5 px-5 border-b border-gray-50">
              <div className="flex items-center gap-2 bg-white/50 backdrop-blur-sm pl-1 pr-3 py-1 rounded-full border border-gray-100 shadow-sm max-w-[200px] overflow-x-auto no-scrollbar">
                {/* Owner + shared avatars */}
                {book?.owner && (
                  <div
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] flex items-center justify-center text-xs font-bold text-white flex-shrink-0 border-2 border-white shadow-sm ring-1 ring-gray-100"
                    title={book.owner.name}
                  >
                    {book.owner.pictureDetails ? (
                      <img src={book.owner.pictureDetails} alt={book.owner.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      book.owner.name ? book.owner.name.charAt(0).toUpperCase() : 'U'
                    )}
                  </div>
                )}
                {sharedWith.map((u) => (
                  <div
                    key={u.id || u.share_id}
                    className={`w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0 border-2 border-white shadow-sm -ml-3 ring-1 ring-gray-50 ${book?.is_shared === false || book?.is_admin !== false ? 'cursor-pointer hover:scale-110' : 'cursor-default'
                      } transition-all`}
                    title={u.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      // is_shared false ise admin'dir, veya is_admin false değilse tıklanabilir
                      if (book?.is_shared === false || book?.is_admin !== false) {
                        handleUserClick(u);
                      }
                    }}
                  >
                    {u.pictureDetails ? (
                      <img src={u.pictureDetails} alt={u.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      u.name ? u.name.charAt(0).toUpperCase() : 'U'
                    )}
                  </div>
                ))}
              </div>

              {/* Aksiyon Butonları */}
              {(book?.is_shared === false || book?.is_admin !== false) && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={startMemoryBookInvite}
                    className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors"
                    title="Davet Et"
                  >
                    <FaShare className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-9 h-9 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"
                    title="Sil"
                  >
                    <FaTrash className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Modal for Add Entry via Button (alternative) */}
            {showAddEntry && (book?.is_shared === false || book?.permission === "write") && (
              // This is just kept if needed, but we have inline input below
              null
            )}

            {/* Entries List Area */}
            {loading && (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-[#3DA7B5] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-medium">Anılar yükleniyor...</p>
              </div>
            )}
            {error && !loading && (
              <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-center">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {!loading && !error && (
              <ResponsiveMasonry
                columnsCountBreakPoints={{ 350: 2, 750: 2 }}
                className="w-full"
              >
                <Masonry columnsCount={2} gutter="12px">
                  {/* New Entry Input Card */}
                  {(book?.is_shared === false || book?.permission === "write") && (
                    <div className="bg-gradient-to-br from-gray-50 to-white w-full rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.03)] border border-gray-100 flex flex-col min-h-[160px] relative group focus-within:ring-2 focus-within:ring-[#3DA7B5]/20 focus-within:border-[#3DA7B5] transition-all duration-300">
                      <div className="flex items-center gap-2 mb-2 opacity-50">
                        <FaPen className="w-3 h-3 text-[#3DA7B5]" />
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Yeni Anı</span>
                      </div>
                      <textarea
                        placeholder="Bugün neler oldu? Anılarına bir not düş..."
                        value={newEntry.content}
                        onChange={(e) => setNewEntry({ content: e.target.value })}
                        rows={4}
                        className="w-full bg-transparent text-sm text-gray-700 leading-relaxed outline-none resize-none placeholder:text-gray-400 placeholder:font-light flex-1"
                      />
                      <div className="mt-2 flex items-center justify-end">
                        <button
                          onClick={handleAddEntry}
                          disabled={submitting || !newEntry.content.trim()}
                          className="w-8 h-8 rounded-full bg-[#3DA7B5] text-white flex items-center justify-center disabled:opacity-50 disabled:scale-90 shadow-md hover:shadow-lg hover:scale-105 transition-all"
                        >
                          {submitting ? <div className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" /> : <FiSend className="w-3.5 h-3.5 ml-0.5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Entry Cards */}
                  {entries.map((entry, index) => (
                    <motion.div
                      key={entry.id || index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleEntryClick(entry)}
                      className="bg-white w-full rounded-2xl p-4 shadow-[0_4px_16px_rgba(0,0,0,0.04)] border border-gray-100 hover:border-[#3DA7B5]/30 hover:shadow-[0_8px_24px_rgba(61,167,181,0.1)] transition-all duration-300 cursor-pointer flex flex-col h-auto group break-inside-avoid mb-3"
                    >
                      <div className="flex-1 mb-3">
                        <p className="text-[13px] text-gray-700 leading-relaxed font-light break-words line-clamp-[8]">
                          {entry.content || "İçerik bulunmuyor"}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <img
                            src={entry.writer?.pictureDetails || "/assets/pngImages/user.png"}
                            alt={entry.writer?.name || "Yazar"}
                            className="w-5 h-5 rounded-full object-cover ring-2 ring-white shadow-sm"
                          />
                          <span className="text-[10px] font-semibold text-gray-500 truncate max-w-[60px]">{entry.writer?.name || "Yazar"}</span>
                        </div>
                        <span className="text-[9px] text-gray-400 font-medium">
                          {new Date(entry.created_at).getDate()} {new Date(entry.created_at).toLocaleDateString('tr-TR', { month: 'short' })}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </Masonry>
              </ResponsiveMasonry>
            )}

            {!loading && !error && entries.length === 0 && (book?.is_shared === true && book?.permission !== "write") && (
              <div className="text-center py-20 px-6">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 grayscale opacity-50">
                  <FiImage className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm">Bu anı defteri henüz boş.</p>
              </div>
            )}

          </div>
        </div>

        {/* Entry Detail Modal */}
        <AnimatePresence>
          {showEntryModal && selectedEntry && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeEntryModal}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-3xl p-0 w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl relative flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Decorative Header */}
                <div className="h-24 bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] relative w-full flex-shrink-0">
                  <button
                    onClick={closeEntryModal}
                    className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors backdrop-blur-md"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                  <div className="absolute -bottom-8 left-6 flex items-end">
                    <div className="w-16 h-16 rounded-full border-4 border-white shadow-md overflow-hidden bg-white">
                      <img
                        src={selectedEntry.writer?.pictureDetails || "/assets/pngImages/user.png"}
                        alt={selectedEntry.writer?.name || "Yazar"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mb-8 ml-3">
                      <h4 className="text-white font-bold text-lg leading-none shadow-black/10 drop-shadow-md">{selectedEntry.writer?.name}</h4>
                    </div>
                  </div>
                </div>

                <div className="pt-10 px-6 pb-6 overflow-y-auto custom-scrollbar">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-6 font-medium">
                    <FaRegCalendarAlt className="w-3.5 h-3.5 text-[#3DA7B5]" />
                    <span>{formatDate(selectedEntry.created_at)}</span>
                  </div>

                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed text-[15px] whitespace-pre-wrap font-light">
                      {selectedEntry.content}
                    </p>
                  </div>
                </div>

                <div className="p-4 border-t border-gray-50 bg-gray-50/50 mt-auto">
                  <button
                    onClick={closeEntryModal}
                    className="w-full py-3 bg-white border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Kapat
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Paylaş Popup */}
        <AnimatePresence>
          {showSharePopup && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSharePopup(false)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-800">Paylaş</h3>
                  <button onClick={() => setShowSharePopup(false)} className="bg-gray-100 p-2 rounded-full text-gray-500 hover:bg-gray-200"><FaTimes /></button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      E-posta Adresi
                    </label>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent outline-none text-gray-800"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      İzin Seviyesi
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setSharePermission("read")}
                        className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${sharePermission === 'read' ? 'bg-[#3DA7B5]/10 border-[#3DA7B5] text-[#3DA7B5]' : 'bg-white border-gray-200 text-gray-600'}`}
                      >
                        Sadece Okuma
                      </button>
                      <button
                        onClick={() => setSharePermission("write")}
                        className={`py-2 px-3 rounded-lg text-sm font-medium border transition-all ${sharePermission === 'write' ? 'bg-[#3DA7B5]/10 border-[#3DA7B5] text-[#3DA7B5]' : 'bg-white border-gray-200 text-gray-600'}`}
                      >
                        Okuma & Yazma
                      </button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={handleShareMemoryBook}
                      disabled={isSharing || !shareEmail.trim()}
                      className="w-full py-3 px-4 bg-[#3DA7B5] text-white rounded-xl font-semibold hover:bg-[#2A8B96] transition-all shadow-lg shadow-[#3DA7B5]/30 disabled:opacity-50 disabled:shadow-none"
                    >
                      {isSharing ? "Gönderiliyor..." : "Davet Gönder"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Sil Onay Popup */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl text-center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaTrash className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Defteri Sil?</h3>
                <p className="text-gray-500 mb-6 text-sm leading-relaxed">
                  Bu anı defterini ve içindeki tüm anıları silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteMemoryBook}
                    disabled={isDeleting}
                    className="flex-1 py-3 px-4 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/30"
                  >
                    {isDeleting ? "Siliniyor..." : "Evet, Sil"}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Permission Popup */}
        <AnimatePresence>
          {showPermissionPopup && selectedUser && (book?.is_shared === false || book?.is_admin !== false) && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPermissionPopup(false)}>
              <motion.div
                initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gray-100 mx-auto mb-3 overflow-hidden border-4 border-white shadow-md">
                    <img
                      src={selectedUser.pictureDetails || "/assets/pngImages/user.png"}
                      alt={selectedUser.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">{selectedUser.name}</h3>
                  <p className="text-sm text-gray-400">{selectedUser.email}</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Yetki Düzenle
                    </label>
                    <div className="flex flex-col gap-2">
                      <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedUser.permission === 'read' ? 'border-[#3DA7B5] bg-[#3DA7B5]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          name="perm"
                          checked={selectedUser.permission === 'read'}
                          onChange={() => setSelectedUser({ ...selectedUser, permission: 'read' })}
                          className="text-[#3DA7B5] focus:ring-[#3DA7B5]"
                        />
                        <span className="text-sm font-medium text-gray-700">Sadece Okuma</span>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedUser.permission === 'write' ? 'border-[#3DA7B5] bg-[#3DA7B5]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          name="perm"
                          checked={selectedUser.permission === 'write'}
                          onChange={() => setSelectedUser({ ...selectedUser, permission: 'write' })}
                          className="text-[#3DA7B5] focus:ring-[#3DA7B5]"
                        />
                        <span className="text-sm font-medium text-gray-700">Okuma ve Yazma</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowPermissionPopup(false);
                        setSelectedUser(null);
                      }}
                      className="flex-1 py-3 px-4 border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleUpdatePermission}
                      disabled={isUpdatingPermission}
                      className="flex-1 py-3 px-4 bg-[#3DA7B5] text-white rounded-xl font-semibold hover:bg-[#2A8B96] transition-colors shadow-lg shadow-[#3DA7B5]/20"
                    >
                      {isUpdatingPermission ? "..." : "Güncelle"}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <div className="h-20"></div>
      </div>
    </div >
  );
}
